import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Layout } from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { QrCode, Download, Loader2, ExternalLink, Copy } from "lucide-react";

const QRCodePage = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/qrcode/pharmacy`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch { toast.error("Erreur de chargement des QR codes"); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const downloadQR = (base64, name) => {
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${base64}`;
    a.download = `qrcode_${name}.png`;
    a.click();
    toast.success(`QR Code ${name} telecharge`);
  };

  const copyURL = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Lien copie !");
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6" data-testid="qrcode-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <QrCode className="w-7 h-7 text-sky-500" />
            QR Codes CMES
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Scannez ou partagez ces QR codes pour un acces rapide a la plateforme
          </p>
        </div>

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* QR Site */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="qr-site-card">
              <div className="p-4 border-b border-slate-100">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-sky-500" />
                  Inscription Pharmacie
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-100">
                    <img
                      src={`data:image/png;base64,${data.qr_site}`}
                      alt="QR Code Inscription"
                      className="w-48 h-48"
                      data-testid="qr-site-image"
                    />
                  </div>
                </div>
                {/* URL Bubble - HIGH CONTRAST */}
                <div className="bg-sky-600 rounded-xl p-4 border border-sky-700 shadow-sm" data-testid="qr-site-bubble">
                  <p className="text-sky-100 text-xs mb-1.5 font-semibold uppercase tracking-wide">Lien d'inscription :</p>
                  <div className="flex items-center gap-2 bg-sky-700/50 rounded-lg px-3 py-2">
                    <code className="text-white text-sm flex-1 truncate font-mono font-medium">{data.urls?.site}</code>
                    <button className="text-sky-200 hover:text-white transition p-1 flex-shrink-0"
                      onClick={() => copyURL(data.urls?.site)} data-testid="copy-site-url">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <button onClick={() => downloadQR(data.qr_site, "inscription")}
                  className="w-full flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white py-2.5 rounded-lg text-sm font-semibold transition" data-testid="download-qr-site">
                  <Download className="w-4 h-4" /> Telecharger
                </button>
              </div>
            </div>

            {/* QR Express */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="qr-express-card">
              <div className="p-4 border-b border-slate-100">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-red-500" />
                  Destruction Express
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-100">
                    <img
                      src={`data:image/png;base64,${data.qr_express}`}
                      alt="QR Code Destruction Express"
                      className="w-48 h-48"
                      data-testid="qr-express-image"
                    />
                  </div>
                </div>
                {/* URL Bubble - HIGH CONTRAST */}
                <div className="bg-red-600 rounded-xl p-4 border border-red-700 shadow-sm" data-testid="qr-express-bubble">
                  <p className="text-red-100 text-xs mb-1.5 font-semibold uppercase tracking-wide">Lien destruction express :</p>
                  <div className="flex items-center gap-2 bg-red-700/50 rounded-lg px-3 py-2">
                    <code className="text-white text-sm flex-1 truncate font-mono font-medium">{data.urls?.express}</code>
                    <button className="text-red-200 hover:text-white transition p-1 flex-shrink-0"
                      onClick={() => copyURL(data.urls?.express)} data-testid="copy-express-url">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <button onClick={() => downloadQR(data.qr_express, "destruction_express")}
                  className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg text-sm font-semibold transition" data-testid="download-qr-express">
                  <Download className="w-4 h-4" /> Telecharger
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5" data-testid="qr-info-bubble">
          <div className="flex items-center gap-4">
            <QrCode className="w-10 h-10 text-sky-400 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-white text-base">Comment utiliser ces QR codes ?</h3>
              <p className="text-sm text-slate-300 mt-1 leading-relaxed">
                Imprimez-les et placez-les dans votre officine pour permettre a vos clients et partenaires
                d'acceder rapidement a la plateforme CMES. Le QR "Destruction Express" permet un acces direct
                au formulaire de demande urgente.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default QRCodePage;
